import { createSlice } from '@reduxjs/toolkit';
import { createAsyncThunkWrapper } from 'redux/thunk/createAsyncThunkWrapper';
import { fetchMembers } from 'services/membersApi';
import { ParishUsersState, ParishUserOption } from 'types/user';

type ParishUsersSliceState = ParishUsersState & {
    inflightParishId: string | null;
};

const initialState: ParishUsersSliceState = {
    records: [],
    loading: false,
    error: null,
    loadedParishId: null,
    inflightParishId: null,
};

/**
 * Member picker for announcements / Send Invoice.
 * Sources options from GET /v1/parishes/:parishId/members.
 * Race safety: fulfilled ignores payloads whose parishId ≠ inflightParishId
 * (supports Send Invoice parish mode where parishId ≠ shell selectedParishId).
 */
export const fetchParishUsers = createAsyncThunkWrapper<{ records: ParishUserOption[]; parishId: string } | null, { parishId: string }>(
    'parishUsers/fetchParishUsers',
    async ({ parishId }) => {
        if (!parishId) return null;
        const res = await fetchMembers(parishId, { page: 1, pageSize: 100 }); // intentional hard cap for autocomplete picker
        return {
            parishId,
            records: res.items.map(
                (m): ParishUserOption => ({
                    id: m.userId,
                    userUUID: m.userId,
                    username: m.email,
                    email: m.email,
                    name: m.name,
                    firstName: m.firstName,
                    lastName: m.lastName,
                    imgUrl: m.imgUrl,
                })
            ),
        };
    }
);

const parishUsersSlice = createSlice({
    name: 'parishUsers',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchParishUsers.pending, (state, action) => {
            state.loading = true;
            state.error = null;
            state.records = [];
            state.inflightParishId = action.meta.arg.parishId;
        });
        builder.addCase(fetchParishUsers.fulfilled, (state, action) => {
            if (!action.payload) {
                if (state.inflightParishId === action.meta.arg.parishId) {
                    state.loading = false;
                }
                return;
            }
            const { records, parishId } = action.payload;
            if (parishId !== state.inflightParishId) return;
            state.loading = false;
            state.records = records;
            state.loadedParishId = parishId;
        });
        builder.addCase(fetchParishUsers.rejected, (state, action) => {
            if (state.inflightParishId === action.meta.arg.parishId) {
                state.loading = false;
                state.error = action.error.message || null;
            }
        });
    },
});

export default parishUsersSlice.reducer;
